import React from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DurationSelectorProps } from './types';
import { DEFAULT_PRESETS } from './constants';
import { useDurationSelector } from './hooks';
import { TwoMonthRange, PresetChips } from './components';

interface InlineRangeSelectorProps extends DurationSelectorProps {
  onApply?: () => void;
  onReset?: () => void;
}

export function InlineRangeSelector({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  minDate,
  maxDate,
  initialMonth,
  sx,
  onApply,
  onReset,
}: InlineRangeSelectorProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    range,
    selectedPreset,
    leftMonth,
    setLeftMonth,
    handleSelectDay,
    handlePresetClick,
    handleReset: resetRange,
    updateRange,
  } = useDurationSelector({
    initialValue: value,
    initialMonth,
    onChange,
  });

  const handleApply = React.useCallback(() => {
    onChange(range);
    onApply?.();
  }, [range, onChange, onApply]);

  const handleResetClick = React.useCallback(() => {
    resetRange();
    onReset?.();
  }, [resetRange, onReset]);

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={0}
      sx={{
        width: isMobile ? '100vw' : 'auto',
        maxWidth: isMobile ? '100vw' : 'none',
        ...sx,
      }}
    >
      {/* Quick ranges section */}
      <PresetChips
        presets={presets}
        selectedPreset={selectedPreset}
        onPresetClick={handlePresetClick}
      />

      {!isMobile && <Divider orientation="vertical" flexItem />}

      {/* Calendar section */}
      <Box
        sx={{
          p: isMobile ? 2 : 1.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allow shrinking
        }}
      >
        <TwoMonthRange
          leftMonth={leftMonth}
          setLeftMonth={setLeftMonth}
          range={range}
          onSelectDay={handleSelectDay}
          minDate={minDate}
          maxDate={maxDate}
        />

        {/* Date display and buttons */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={isMobile ? 2 : 2}
          sx={{
            mt: 2,
            ...(isMobile
              ? { alignItems: 'stretch' }
              : { justifyContent: 'flex-end', alignItems: 'center' }),
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              ...(isMobile && { justifyContent: 'center', mb: 1 }),
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Selected: {range[0]?.format('DD MMM YYYY') || '—'} –{' '}
              {range[1]?.format('DD MMM YYYY') || '—'}
            </Typography>
            {range[0] && range[1] && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                (
                {range[1]!.endOf('day').diff(range[0]!.startOf('day'), 'day') +
                  1}{' '}
                days)
              </Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              ...(isMobile && { justifyContent: 'center' }),
            }}
          >
            <Button
              variant="outlined"
              size={isMobile ? 'medium' : 'small'}
              onClick={handleResetClick}
              sx={{ minWidth: isMobile ? 100 : 'auto' }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'small'}
              onClick={handleApply}
              disabled={!range[0] || !range[1]}
              sx={{ minWidth: isMobile ? 100 : 'auto' }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
